import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Body } from 'syf-component-library/ui/typography';
import styled from 'styled-components';
import query from 'syf-component-library/const/mediaQueries';
import { Inset } from 'syf-component-library/ui/spacing';
import { generateUUID, getLanguageFromStorage } from 'syf-js-utilities';
import { useAccountSummaryV2Query } from '@syf-api-library/account-summary';
import { t } from '../../translations/getTranslation';
import ModalImage from '../../components/ModalImage';
import useAccountActivity from '../../hooks/useAccountActivity';
import useConfig from '../../context/config.context';
import useAccountSummary from '../../hooks/useAccountSummary';
import useAnalytics from '../../hooks/useAnalytics';
import RenderTransactions from './RenderTransactions';
const InsetContainer = styled(Inset) `
  display: flex;
  align-items: center;
  border-bottom: 2px solid ${({ theme }) => theme.colors.aliceBlue};
  padding: ${({ theme }) => `${theme.spacing.medium}px ${theme.spacing.base}px`};
  @media ${query.greaterThanMedium} {
    padding: ${({ theme }) => `${theme.spacing.large}px ${theme.spacing.base}px`};
  }
`;
const CardInfoWrapper = styled.div `
  display: flex;
  flex-direction: row;
`;
const CardInfo = styled.div `
  display: flex;
  flex-direction: column;
  margin-left: ${({ theme }) => theme.spacing.base}px;
`;
const ImageWrapper = styled.div `
  width: 88px;
  height: 56px;
`;
const Image = styled.img `
  height: auto;
  width: auto;
`;
const DescriptionContainer = styled(Body) `
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  font-size: ${({ theme }) => theme.fontSizes.medium}px;
  line-height: ${({ theme }) => theme.lineHeights.root};
`;
const AccountInfo = styled(Body) `
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  font-size: ${({ theme }) => theme.fontSizes.small}px;
  line-height: ${({ theme }) => theme.lineHeights.small}px;
  @media ${query.greaterThanMedium} {
    font-size: ${({ theme }) => theme.fontSizes.root}px;
    line-height: ${({ theme }) => theme.lineHeights.root};
  }
`;
const Content = styled.div `
  max-width: 504px;
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.xxlarge}px ${theme.spacing.large}px`};
  padding-bottom: ${({ theme }) => theme.spacing.large}px;
  margin: 0 auto;
  @media ${query.greaterThanMedium} {
    max-width: 652px;
  }
`;
const Title = styled.h2 `
  font-size: ${({ theme }) => theme.fontSizes.medium}px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: ${({ theme }) => theme.lineHeights.medium};
`;
const Paragraph = styled(Body) `
  font-size: ${({ theme }) => theme.fontSizes.medium}px;
  line-height: ${({ theme }) => theme.lineHeights.medium};
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  padding: 0;
  margin-bottom: ${({ theme }) => theme.spacing.medium}px;
`;
const NoTransactionText = styled(Body) `
  font-size: ${({ theme }) => theme.fontSizes.root}px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  line-height: ${({ theme }) => theme.lineHeights.root}px;
`;
const ImageContainer = styled.div `
  display: flex;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.spacing.xxlarge}px;
`;
const NoTransactions = styled.div `
  border-top: 2px solid ${({ theme }) => theme.colors.aliceBlue};
  text-align: center;
  padding: ${({ theme }) => `${theme.spacing.large}px ${theme.spacing.base}px`};
`;
const TransactionsTable = styled.div `
  display: none;
  flex-direction: row;
  border-top: 1px solid ${({ theme }) => theme.colors.lightGrey};
  @media ${query.greaterThanMedium} {
    padding-right: ${({ theme }) => theme.spacing.large}px;
    display: flex;
  }
`;
const TableHeader = styled(Body) `
  font-size: ${({ theme }) => theme.fontSizes.root}px;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  line-height: ${({ theme }) => theme.lineHeights.root}px;
  padding-top: ${({ theme }) => theme.spacing.large}px;
  padding-bottom: ${({ theme }) => theme.spacing.large}px;
`;
const DateHeader = styled(TableHeader) `
  margin-left: 90px;
  margin-right: 50px;
`;
const HeaderPadding = styled.div `
  display: flex;
  justify-content: space-between;
  flex: 1;
`;
const LeftSection = styled.div `
  display: flex;
  align-self: flex-start;
`;
const RightSection = styled.div `
  display: flex;
  align-self: flex-end;
`;
const RecognizeTransactions = () => {
    const currentLang = getLanguageFromStorage();
    const { transactions, isSuccess: transactionsDataSuccess } = useAccountActivity(currentLang);
    const hasTransactions = transactions.length;
    const last4digits = '1234';
    const accountName = 'TruRetail Card';
    const { config, accountId } = useConfig();
    const { isSuccess: accountDataSuccess } = useAccountSummaryV2Query({
        config,
        keyProps: { accountId, accountIdType: 'guid' }
    });
    const { isSemu } = useAccountSummary();
    useAnalytics(transactionsDataSuccess && accountDataSuccess, {
        hasTransactions,
        isSemu
    });
    return (_jsxs(_Fragment, { children: [_jsx(InsetContainer, Object.assign({ all: "base" }, { children: _jsxs(CardInfoWrapper, { children: [_jsx(ImageWrapper, { children: _jsx(Image, { src: "https://qa.synchronycredit.com/assets/cardArt/syf_generic_card.svg", alt: "credit card image" }) }), _jsxs(CardInfo, { children: [_jsx(DescriptionContainer, { children: accountName }), _jsx(AccountInfo, { children: t('common.accountEndingIn', { last4digits }) })] })] }) })), _jsxs(Content, { children: [_jsx(ImageContainer, { children: _jsx(ModalImage, { imageName: "DISPUTE_TRANSACTION" }) }), _jsx(Paragraph, { children: t('recognizeTransaction.previousCardActivated') }), _jsx(Title, { children: t('recognizeTransaction.doYouRecognize') })] }), hasTransactions ? (_jsxs(_Fragment, { children: [_jsx(TransactionsTable, { children: _jsxs(HeaderPadding, { children: [_jsxs(LeftSection, { children: [_jsx(DateHeader, { children: t('common.date') }), _jsx(TableHeader, { children: t('common.description') })] }), _jsx(RightSection, { children: _jsx(TableHeader, { children: t('common.amount') }) })] }) }), transactions.map((transaction) => (_jsx(RenderTransactions, { transaction: transaction }, generateUUID())))] })) : (_jsx(NoTransactions, { children: _jsx(NoTransactionText, { children: t('cantVerify.noTransactionsFound') }) }))] }));
};
export default RecognizeTransactions;
//# sourceMappingURL=index.js.map